/**
 * Light or dark, chosen in the console rather than only by the device.
 *
 * The operators are on shared machines and on phones whose system theme is not
 * theirs to change; "the console is blinding at night" and "I cannot read it in
 * the warehouse" were both about the same missing switch.
 *
 * The stylesheets no longer test `prefers-color-scheme` themselves: every dark
 * rule hangs off `:root[data-theme="dark"]`, and this module is what puts that
 * attribute there — resolving "system" to a concrete value and following the
 * device while it stays on "system". The same three lines run in the page head
 * (index.html, __dev.html) before the bundle loads, so the first paint is
 * already the right colour; keep the key and the logic in step with them.
 */
import { useSyncExternalStore } from "react";

export type ThemePref = "system" | "light" | "dark";

export const THEME_KEY = "lazior_theme";

const media = () => window.matchMedia("(prefers-color-scheme: dark)");

export function readThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* private mode, or storage blocked */
  }
  return "system";
}

/** Puts the resolved theme on <html>, which is what the CSS reads. */
export function applyTheme(pref: ThemePref): void {
  const dark = pref === "dark" || (pref === "system" && media().matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === "system") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* the choice still applies to this tab */
  }
  applyTheme(pref);
  chosen = pref;
  for (const f of listeners) f();
}

// One choice for the whole page: the account menu and the phone's 更多 each
// draw a switch, and each used to keep its own copy — set one, and the other
// still showed the old choice until the page was reloaded.
let chosen: ThemePref | null = null;
const listeners = new Set<() => void>();
const listen = (f: () => void) => {
  listeners.add(f);
  return () => {
    listeners.delete(f);
  };
};

/** The choice now, read once from storage and then kept here. */
export function themePrefNow(): ThemePref {
  return (chosen ??= readThemePref());
}

/** The choice, redrawn whenever any switch changes it. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore(listen, themePrefNow);
}

/**
 * Follows the device while the choice is "system". Returns the unsubscribe.
 * Without this, a phone switching to night mode at sunset would leave the
 * console in day colours until it was reloaded.
 */
export function watchSystemTheme(getPref: () => ThemePref): () => void {
  const m = media();
  const onChange = () => {
    if (getPref() === "system") applyTheme("system");
  };
  m.addEventListener("change", onChange);
  return () => m.removeEventListener("change", onChange);
}
